/**
 * The segment definition editor: nested AND/OR groups of rules, up to four
 * levels deep, over the field catalogue the server publishes.
 *
 * It holds NO state of its own — `value` in, `onChange` out — so the page that
 * mounts it owns saving, dirtiness and the live preview, and two mounts can
 * never disagree about what the definition is.
 *
 * `errors` is keyed by the server's 422 paths (`definition.rules.0.value`).
 * Both the `definition.`-prefixed form and the bare form are read, so a caller
 * that strips the prefix and one that does not get the same marks.
 */
import { Button, SegmentedControl } from '@/components/ui';
import { cx } from '@/lib/cx';
import type { SegmentDefinition, SegmentField, SegmentGroup, SegmentRule } from '@/lib/types';
import { RuleEditor } from './RuleEditor';
import { defaultRuleFor } from './ref-options';

export const MAX_SEGMENT_DEPTH = 4;

type Node = SegmentRule | SegmentGroup;

function isGroup(node: Node): node is SegmentGroup {
  return typeof node === 'object' && node !== null && 'rules' in node && Array.isArray((node as SegmentGroup).rules);
}

export interface SegmentBuilderProps {
  audience: 'contacts' | 'subscribers';
  value: SegmentDefinition;
  onChange(v: SegmentDefinition): void;
  fields: SegmentField[];
  disabled?: boolean;
  errors?: Record<string, string>;
}

export function SegmentBuilder({ value, onChange, fields, disabled = false, errors = {} }: SegmentBuilderProps) {
  return (
    <GroupEditor
      group={value}
      onChange={onChange}
      fields={fields}
      depth={1}
      path="rules"
      disabled={disabled}
      errors={errors}
    />
  );
}

function errorAt(errors: Record<string, string>, path: string): string | undefined {
  return errors[`definition.${path}`] ?? errors[path];
}

interface GroupEditorProps {
  group: SegmentGroup;
  onChange(group: SegmentGroup): void;
  onRemove?: () => void;
  fields: SegmentField[];
  depth: number;
  /** The path to this group's `rules` array, without the `definition.` prefix. */
  path: string;
  disabled: boolean;
  errors: Record<string, string>;
}

function GroupEditor({ group, onChange, onRemove, fields, depth, path, disabled, errors }: GroupEditorProps) {
  const groupPath = path === 'rules' ? '' : path.replace(/\.rules$/, '');
  const groupError = groupPath ? errorAt(errors, groupPath) : (errors.definition ?? errors['definition.rules']);
  const rulesError = errorAt(errors, path);

  const setRule = (index: number, next: Node) => {
    const rules = group.rules.slice();
    rules[index] = next;
    onChange({ ...group, rules });
  };
  const removeRule = (index: number) => {
    onChange({ ...group, rules: group.rules.filter((_, i) => i !== index) });
  };
  const firstField = fields[0];
  const addRule = () => {
    if (!firstField) return;
    onChange({ ...group, rules: [...group.rules, defaultRuleFor(firstField)] });
  };
  const addGroup = () => {
    if (!firstField) return;
    onChange({
      ...group,
      rules: [...group.rules, { match: group.match === 'all' ? 'any' : 'all', rules: [defaultRuleFor(firstField)] }],
    });
  };

  return (
    <div
      className={cx(
        // `min-w-0` on every nested grid item so a deep group can shrink to the
        // panel instead of widening it — which is what pushed controls off screen.
        'grid min-w-0 gap-3 rounded-md border p-3',
        depth === 1 ? 'border-border bg-surface' : 'border-hairline-strong bg-surface-sunken',
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-fg-secondary">
          <span>Match</span>
          {/* Fixed width so "Match [All | Any] of these" stays one line: the
              control is `w-full` and would otherwise claim a line of its own. */}
          <div className="w-36 shrink-0">
            <SegmentedControl
              label="Match"
              value={group.match}
              options={[
                { value: 'all', label: 'All' },
                { value: 'any', label: 'Any' },
              ]}
              onChange={(match) => onChange({ ...group, match })}
              disabled={disabled}
            />
          </div>
          <span>of these</span>
        </div>
        {onRemove && (
          <Button variant="ghost" size="sm" className="ml-auto" onClick={onRemove} disabled={disabled}>
            Remove group
          </Button>
        )}
      </div>

      {group.rules.length === 0 && (
        <p className="m-0 text-xs text-fg-muted">No rules in this group.</p>
      )}

      <ol className="m-0 grid min-w-0 list-none gap-3 p-0">
        {group.rules.map((node, index) => {
          const childPath = `${path}.${index}`;
          return (
            <li key={index} className="grid min-w-0 gap-1">
              {index > 0 && (
                <span className="text-xs font-medium text-fg-muted">
                  {group.match === 'all' ? 'and' : 'or'}
                </span>
              )}
              {isGroup(node) ? (
                <GroupEditor
                  group={node}
                  onChange={(next) => setRule(index, next)}
                  onRemove={() => removeRule(index)}
                  fields={fields}
                  depth={depth + 1}
                  path={`${childPath}.rules`}
                  disabled={disabled}
                  errors={errors}
                />
              ) : (
                <RuleEditor
                  rule={node}
                  fields={fields}
                  onChange={(next) => setRule(index, next)}
                  onRemove={() => removeRule(index)}
                  disabled={disabled}
                  errors={{
                    field: errorAt(errors, `${childPath}.field`),
                    op: errorAt(errors, `${childPath}.op`),
                    value: errorAt(errors, `${childPath}.value`),
                    rule: errorAt(errors, childPath),
                  }}
                />
              )}
            </li>
          );
        })}
      </ol>

      {(groupError ?? rulesError) && <p className="m-0 text-xs text-danger">{groupError ?? rulesError}</p>}

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={addRule} disabled={disabled || !firstField}>
          Add rule
        </Button>
        <span
          title={depth >= MAX_SEGMENT_DEPTH ? `Groups nest at most ${MAX_SEGMENT_DEPTH} levels deep.` : undefined}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={addGroup}
            disabled={disabled || !firstField || depth >= MAX_SEGMENT_DEPTH}
          >
            Add group
          </Button>
        </span>
      </div>
    </div>
  );
}
